import React, { useContext, useState } from 'react';
import Button from '../../components/shared/custom-btn';
import CustomInput from '../../components/shared/custom-input';
import { RxCross2 } from 'react-icons/rx';
import DateTimeLocalInput from '../../components/shared/datetime-input';
import CustomTextarea from '../../components/shared/custom-textarea';
import CustomReactSelect from '../../components/shared/custom-react-selector';
import ModalHeading from '../../components/shared/modal-heading';
import { prependDefault } from '../../utils/structure-options-utils';
import { AccountsContext } from '../../components/context/account-context';
import { ApiResponseMessage } from '../../components/shared/api-response';
import { localDateTimeToUTC } from '../../utils/set-date-range-utils';
import { createServiceConfig } from '../../services/service-config-service';

const AddModal = ({
    vehicle,
    handleCloseAddModal,
    onAdd,
    openEditModal,
    serviceTypes,
    triggerTypes,
    fetchServiceConfig,
    vehicles,
    isVehicleEditable = true,
}) => {
    const { accountId } = useContext(AccountsContext);


    const [formData, setFormData] = useState({
        vehicleId: vehicle ? vehicle.vehicleId : '',
        serviceType: '',
        triggerType: '',
        start: null,
        period: '',
        note: '',
    });
    const [loading, setLoading] = useState(false);
    const [apiResponse, setApiResponse] = useState({ message: '', type: '' });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setApiResponse({ message: '', type: '' });
    };

    const handleSelectChange = (selectedOption, { name }) => {
        setFormData((prev) => ({
            ...prev,
            [name]: selectedOption ? selectedOption.value : '',
        }));
        setApiResponse({ message: '', type: '' });
    };

    const validateForm = () => {
        if (!formData.vehicleId) return 'Vehicle is required';
        if (!formData.serviceType) return 'Service Type is required';
        if (!formData.triggerType) return 'Trigger Type is required';
        if (!formData.start && formData.start !== 0) return 'Start is required';
        if (!formData.period && formData.period !== 0) return 'Period is required';
        if (formData.triggerType !== 'serverTime' && isNaN(parseInt(formData.start)))
            return 'Start must be a valid number for non-serverTime triggers';
        if (isNaN(parseInt(formData.period))) return 'Period must be a valid number';
        return null;
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (loading || apiResponse.type === 'success') return;
        const validationError = validateForm();
        if (validationError) {
            setApiResponse({ message: validationError, type: 'error' });
            return;
        }

        setLoading(true);
        setApiResponse({ message: '', type: '' });

        try {
            const serviceConfigData = {
                vehicleId: formData.vehicleId,
                serviceType: formData.serviceType,
                triggerType: formData.triggerType,
                start:
                    formData.triggerType === 'serverTime'
                        ? localDateTimeToUTC(formData.start)
                        : parseInt(formData.start),
                period: parseInt(formData.period),
                note: formData.note,
            };

            const response = await createServiceConfig(serviceConfigData, accountId);

            if (response.success) {
                setApiResponse({
                    message: 'Service Config record created successfully!',
                    type: 'success',
                });
                const newItem = response.data;
                onAdd?.(newItem);
                fetchServiceConfig?.('ServiceConfig');
                // Only call openEditModal if explicitly needed
                if (openEditModal) openEditModal(newItem);
                setTimeout(() => {
                    handleCloseAddModal();
                }, 1000);
            } else {
                setApiResponse({
                    message: response.data?.message || 'Failed to create service config record',
                    type: 'error',
                });
            }
        } catch (error) {
            setApiResponse({
                message: error.message || 'An unexpected error occurred',
                type: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const vehicleOptions = prependDefault(vehicles.map((v) => ({ value: v.vehicleId, label: v.label })), 'Vehicle');
    const serviceTypeOptions = prependDefault(serviceTypes.map((v) => ({ value: v.slug, label: v.label })), 'Service Types');
    const triggerTypeOptions = prependDefault(triggerTypes.map((v) => ({ value: v.slug, label: v.label })), 'Trigger Types');

    return (
        <form onSubmit={handleSave} className="space-y-2 pt-3 relative">
            <ModalHeading className="text-left mb-3 border-b border-gray-300 px-4 pb-2">
                Add Service Configuration Detail
            </ModalHeading>

            <button
                className="hover:scale-105 absolute -top-2 right-2 bg-white border p-2 rounded-full"
                onClick={handleCloseAddModal}
                disabled={loading}
            >
                <RxCross2 className="text-xl" />
            </button>

            <ApiResponseMessage response={apiResponse} />

            <div className="grid grid-cols-1 sm:grid-cols-2 px-4 gap-y-1.5 gap-x-2">
                <CustomReactSelect
                    label="Vehicle"
                    name="vehicleId"
                    value={vehicleOptions.find((opt) => opt.value === formData.vehicleId)}
                    onChange={handleSelectChange}
                    options={vehicleOptions}
                    placeholder='Select vehicle'
                    required={true}
                    isEditable={isVehicleEditable}
                    isDisabled={loading}
                />

                <CustomReactSelect
                    label="Service Type"
                    name="serviceType"
                    value={serviceTypeOptions.find((opt) => opt.value === formData.serviceType)}
                    onChange={handleSelectChange}
                    placeholder='Select service type'
                    options={serviceTypeOptions}
                    required={true}
                    isDisabled={loading}
                />

                <CustomReactSelect
                    label="Trigger Type"
                    name="triggerType"
                    value={triggerTypeOptions.find((opt) => opt.value === formData.triggerType)}
                    onChange={handleSelectChange}
                    options={triggerTypeOptions}
                    placeholder='Select trigger type'
                    required={true}
                    isDisabled={loading}
                />

                {formData.triggerType === 'serverTime' ? (
                    <DateTimeLocalInput
                        label="Start"
                        name="start"
                        value={formData.start}
                        onChange={handleChange}
                        required={true}
                        disabled={loading}
                    />
                ) : (
                    <CustomInput
                        label="Start"
                        name="start"
                        type="number"
                        inputType="number"
                        value={formData.start}
                        onChange={handleChange}
                        inputStyle=""
                        labelGap="!gap-0.5"
                        labelStyle="text-gray-700"
                        required={true}
                        maxLength={7}
                        disabled={loading}
                    />
                )}

                <CustomInput
                    label="Period (km/mi, hours, days)"
                    name="period"
                    type="number"
                    inputType="number"
                    value={formData.period}
                    onChange={handleChange}
                    inputStyle=""
                    labelGap="!gap-0.5"
                    labelStyle="text-gray-700"
                    required={true}
                    maxLength={7}
                    disabled={loading}
                />

                <div className="w-full sm:col-span-2">
                    <CustomTextarea
                        label="Note"
                        name="note"
                        value={formData.note}
                        onChange={handleChange}
                        placeholder=""
                        rows={2}
                        labelStyle="text-gray-700"
                        maxLength={500}
                        disabled={loading}
                    />
                </div>
            </div>

            <div className="flex justify-end px-4">
                <Button
                    label={loading ? 'Saving...' : 'Save'}
                    loading={loading}
                    loadingLabel="Saving..."
                    type="submit"
                    disabled={loading || apiResponse.type === 'success'}
                    className="bg-btnBg rounded-lg !py-1 text-white"
                />
            </div>
        </form>
    );
};

export default AddModal;